import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface TenantCardData {
  tenant_id: string;
  name: string;
  slug: string;
  tier: string;
  tenant_status: string;
  relationship_id: string;
  status: string;
  accepted_at: string | null;
  role: string | null;
}

interface TenantCardProps {
  tenant: TenantCardData;
  className?: string;
}

export function TenantCard({ tenant, className }: TenantCardProps) {
  const navigate = useNavigate();
  const connectedAt = tenant.accepted_at
    ? formatDistanceToNow(parseISO(tenant.accepted_at), { addSuffix: true })
    : 'Pending';

  return (
    <Card
      className={cn(
        'group cursor-pointer transition-all hover:shadow-md hover:border-accent-cyan/30',
        className,
      )}
      onClick={() => navigate(`/portal/tenants/${tenant.slug}/dashboard`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-surface-2 group-hover:bg-accent-cyan/10 transition-colors">
              <Building2 className="h-4 w-4 text-text-secondary group-hover:text-accent-cyan" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-text-primary truncate">{tenant.name}</p>
              <p className="text-xs text-text-secondary truncate">{tenant.slug}</p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-text-secondary shrink-0 mt-0.5 group-hover:text-accent-cyan transition-colors" />
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {tenant.role && (
            <Badge variant="outline" className="text-xs text-text-secondary border-border-subtle">
              {tenant.role}
            </Badge>
          )}
          <span className="ml-auto text-xs text-text-secondary">{connectedAt}</span>
        </div>
      </CardContent>
    </Card>
  );
}
